import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Stack,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Refresh, Stop } from "@mui/icons-material";
import { useOdooMigrationPermissions } from "../../hooks/useOdooMigrationPermissions";
import { odooProductionMigrationService } from "../../services/odooProductionMigrationService";
import { MigrationProgressStepper } from "./MigrationProgressStepper";
import type { ProductionMigrationStatusDto } from "../../types/odoo-migration";

interface OdooProductionMigrationMonitorProps {
  refreshInterval?: number;
  onStatusChange?: (status: ProductionMigrationStatusDto) => void;
}

export function OdooProductionMigrationMonitor({
  refreshInterval = 2000,
  onStatusChange,
}: OdooProductionMigrationMonitorProps) {
  const { canCancel, isLoading: permissionsLoading } =
    useOdooMigrationPermissions();
  const [status, setStatus] = useState<ProductionMigrationStatusDto | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const stopPollingRef = useRef<(() => void) | null>(null);

  const fetchStatus = async () => {
    try {
      setError(null);
      const currentStatus =
        await odooProductionMigrationService.getMigrationStatus();
      setStatus(currentStatus);
      onStatusChange?.(currentStatus);
    } catch (err: any) {
      const errorMessage =
        (err as Error)?.message || String(err) || "Error desconocido";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const startPolling = () => {
    if (stopPollingRef.current) {
      stopPollingRef.current();
    }

    const cleanup = odooProductionMigrationService.startStatusPolling(
      (newStatus) => {
        setStatus(newStatus);
        setIsLoading(false);
        onStatusChange?.(newStatus);
      },
      refreshInterval,
    );

    stopPollingRef.current = cleanup;
  };

  const handleCancel = async () => {
    try {
      await odooProductionMigrationService.cancelMigration();
      await fetchStatus();
    } catch (err: any) {
      const errorMessage =
        (err as Error)?.message || String(err) || "Error al cancelar";
      setError(`Error al cancelar: ${errorMessage}`);
    }
  };

  useEffect(() => {
    fetchStatus().then(() => {
      startPolling();
    });

    return () => {
      if (stopPollingRef.current) {
        stopPollingRef.current();
      }
    };
  }, []);

  if (permissionsLoading || isLoading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <LinearProgress />
          <Typography sx={{ mt: 2 }}>Cargando estado...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={fetchStatus}>
                <Refresh /> Reintentar
              </Button>
            }
          >
            Error al obtener estado: {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Alert severity="info">
            No hay informacion de sincronizacion disponible
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Toolbar: refresh + cancel */}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Tooltip title="Actualizar estado">
          <IconButton onClick={fetchStatus} size="small">
            <Refresh />
          </IconButton>
        </Tooltip>
        {status.isRunning && canCancel && (
          <Tooltip title="Cancelar sincronizacion">
            <IconButton onClick={handleCancel} color="error" size="small">
              <Stop />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Progress stepper */}
      <MigrationProgressStepper status={status} />
    </Stack>
  );
}
